import { createFileRoute, Link } from "@tanstack/react-router";
import { LiveAnnouncementBanner } from "@/components/home/LiveAnnouncementBanner";
import { PromotionalBanner } from "@/components/home/PromotionalBanner";
import { HeroMedia } from "@/components/home/HeroMedia";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { WhyChoose } from "@/components/home/WhyChoose";
import { SampleDishes } from "@/components/home/SampleDishes";
import { TodaysMenu } from "@/components/home/TodaysMenu";
import { DeliveryAreaSelector } from "@/components/home/DeliveryAreaSelector";
import { HowItWorks } from "@/components/home/HowItWorks";
import { AlacarteComingSoon } from "@/components/mp2/AlacarteComingSoon";
import { TrustQuality } from "@/components/home/TrustQuality";
import { FounderPreview } from "@/components/home/FounderPreview";
import { TestimonialSlider } from "@/components/home/TestimonialSlider";
import { JournalPreview } from "@/components/home/JournalPreview";
import { FaqPreview } from "@/components/home/FaqPreview";
import { FinalCta } from "@/components/home/FinalCta";
import { RecognitionsSection } from "@/components/home/RecognitionsSection";
import { TodaysSpecialsVideos } from "@/components/home/TodaysSpecialsVideos";
import { HomepageCouponStrip } from "@/components/home/HomepageCouponStrip";
import { useHomepageContent } from "@/hooks/useHomepageContent";
import { CmsCtaLink } from "@/components/CmsCtaLink";
import { HOMEPAGE_FALLBACKS } from "@/lib/homepage-defaults";
import { useImageFallback } from "@/hooks/useImageFallback";
import { canonicalUrl } from "@/lib/seo/site";

const META_TITLE = "Healthy Meal Delivery in Mumbai | Poonam Kapur Healthy Kitchen";
const META_DESCRIPTION = "Explore chef-curated meal plans and à-la-carte meals from Poonam Kapur Healthy Kitchen, a tiffin service in Mumbai. Contact us to confirm delivery to your area.";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: META_TITLE },
      {
        name: "description",
        content:
          META_DESCRIPTION,
      },
      {
        property: "og:title",
        content: META_TITLE,
      },
      {
        property: "og:description",
        content:
          META_DESCRIPTION,
      },
      { property: "og:url", content: canonicalUrl("/") },
      { property: "og:type", content: "website" },
      {
        property: "og:image",
        content: canonicalUrl("/og-home.jpg"),
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: META_TITLE,
      },
      {
        name: "twitter:description",
        content:
          META_DESCRIPTION,
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl("/") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Which areas do you deliver to?", acceptedAnswer: { "@type": "Answer", text: "We deliver across 70+ Mumbai neighbourhoods including Andheri, Bandra, BKC, Juhu, Powai and Worli." } },
            { "@type": "Question", name: "What meal timings do you offer?", acceptedAnswer: { "@type": "Answer", text: "Four slots daily: Breakfast, Lunch, Snacks and Dinner. Each slot is prepared and dispatched fresh for that window." } },
            { "@type": "Question", name: "What are the order cutoff times?", acceptedAnswer: { "@type": "Answer", text: "Breakfast — order by 8:00 pm the previous day. Lunch — 6:00 am same day. Snacks — 8:30 pm previous day. Dinner — 12:01 pm same day." } },
            { "@type": "Question", name: "How do you ensure freshness?", acceptedAnswer: { "@type": "Answer", text: "Meals are cooked to the slot they're delivered in — never reheated batches. Farm-direct ingredients and short cook-to-delivery windows keep meals fresh." } },
            { "@type": "Question", name: "What kind of packaging do you use?", acceptedAnswer: { "@type": "Answer", text: "Food-grade, hygienic packaging designed for safe, fresh, in-city delivery." } },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

const SLOTS = [
  { name: "Breakfast", cutoff: "Order by 8:00 pm, previous day" },
  { name: "Lunch", cutoff: "Order by 6:00 am, same day" },
  { name: "Snacks", cutoff: "Order by 8:30 pm, previous day" },
  { name: "Dinner", cutoff: "Order by 12:01 pm, same day" },
];

function Index() {
  const content = useHomepageContent();
  const heroFallback = useImageFallback(HOMEPAGE_FALLBACKS.heroImageUrl);
  return (
    <SiteLayout>
      <LiveAnnouncementBanner enabled={content.liveAnnouncementEnabled === true} />
      <PromotionalBanner promotion={content.promotion ?? null} />
      {/* Hero */}
      <section className="relative overflow-hidden pb-16 md:pb-20">
        {/* Full-bleed hero media — no card, border, frame or image underlay */}
        <div className="relative mx-auto w-full max-w-[95%] overflow-hidden sm:max-w-[94%] md:max-w-[92%] lg:max-w-[75%]">
          {content.isLoaded ? (
            <HeroMedia
              imageUrl={content.heroImageUrl}
              imageAlt={content.heroImageAlt}
              onImageError={heroFallback.onError}
              mediaType={content.heroMediaType}
              videoUrl={content.heroVideoUrl}
              videoMobileUrl={content.heroVideoMobileUrl}
              posterUrl={content.heroPosterUrl}
              className="aspect-[4/5] w-full object-cover object-center sm:aspect-[16/9] lg:aspect-[2/1]"
            />
          ) : (
            // CMS media type not known yet: reserve the hero box without
            // flashing the fallback image ahead of a configured video.
            <div aria-hidden className="aspect-[4/5] w-full sm:aspect-[16/9] lg:aspect-[2/1]" />
          )}
        </div>

        {/* Hero copy — below the video, centered on the homepage content width */}
        <Container>
          <div className="mx-auto mt-12 max-w-3xl text-center md:mt-16">
            <span className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {content.heroBadge}
            </span>
            <h1 className="mt-6 font-display text-5xl leading-[1.05] font-medium md:text-6xl lg:text-7xl">
              {content.heroTitleLine1}<br />
              <span className="text-primary italic">{content.heroTitleAccent}</span> {content.heroTitleLine2}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              {content.heroSubtitle}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <CmsCtaLink
                href={content.primaryButtonLink}
                className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                {content.primaryButtonText}
              </CmsCtaLink>
              <CmsCtaLink
                href={content.secondaryButtonLink}
                className="inline-flex items-center rounded-full border border-border bg-background px-6 py-3 text-sm font-medium text-foreground hover:bg-secondary"
              >
                {content.secondaryButtonText}
              </CmsCtaLink>
            </div>
          </div>
        </Container>
      </section>

      {/* Coupon promotion — immediately below the hero video */}
      <HomepageCouponStrip />

      <TodaysSpecialsVideos />

      {/* Meal slot strip */}
      <Section className="border-y border-border/60 bg-secondary/30 py-14">
        <div className="grid gap-8 md:grid-cols-4">
          {SLOTS.map((s) => (
            <div key={s.name}>
              <div className="font-display text-2xl font-medium">{s.name}</div>
              <p className="mt-2 text-sm text-muted-foreground">{s.cutoff}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Delivery area selector */}
      <Section className="py-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Delivery
            </span>
            <h2 className="mt-2 font-display text-2xl font-medium md:text-3xl">
              Where should we deliver?
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Healthy meal delivery in Mumbai, with freshly prepared meals for your day. We serve selected neighbourhoods, including Andheri, Bandra, BKC, Juhu, Powai and Worli.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Looking for a tiffin service in Mumbai? Explore our{" "}
              <Link to="/meal-plans" className="text-primary underline underline-offset-4">meal plans</Link>, browse the{" "}
              <Link to="/weekly-menu" className="text-primary underline underline-offset-4">weekly menu</Link>, or{" "}
              <Link to="/contact" className="text-primary underline underline-offset-4">confirm delivery to your exact location</Link>.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Questions before ordering? Read our{" "}
              <Link to="/faq" className="text-primary underline underline-offset-4">meal and delivery FAQs</Link>.
            </p>
          </div>
          <DeliveryAreaSelector />
        </div>
      </Section>

      <AlacarteComingSoon />

      <TodaysMenu />

      <WhyChoose />
      <HowItWorks />
      <SampleDishes />
      <TrustQuality />

      <FounderPreview />
      <TestimonialSlider />
      <JournalPreview />
      <FaqPreview />
      <RecognitionsSection />
      <FinalCta />
    </SiteLayout>
  );
}
